import React, { useContext, useEffect, useState } from 'react';
import { Loan, Task, TaskResponse, TaskState } from 'gql/graphql';
import { CardLoanTasksProps } from 'components/Atoms/Cards/CardLoanTasks/CardLoanTasks';
import EntityDocs from './EntityDocs';
import { ENTITY_DOCS_LABEL, ENTITY_DOCS_ORDER_CONFIRM, ENTITY_DOCS_RESPONSES } from './responses.constants';
import { ModuleKeys } from 'services/task/modules-keys.constanst';
import { useGetEntitiesByOwner } from '../../../../../services/entity/querys';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import { TaskOpenModalContext } from 'scenes/Private/Private';
import CircleTag from '../../../../../components/Atoms/Tags/CircleTag/CircleTag';

interface Props {
  loan: Loan | any;
  tasks?: Task[] | any[],
  ownerSelect: string;
}

const useCardEntityDocs = ({ tasks, ownerSelect, loan }: Props) => {

  const { setHandler: setTaskOpenModal }: any = useContext(TaskOpenModalContext);
  
  const { data: entities, reexecute: getEntitiesByOwner } = useGetEntitiesByOwner(
    {
      variables: { ownerId: ownerSelect },
      pause: true
    }
  );

  const [dataTask, setDataTask] = useState<Task>();
  const [stepNow, setStepNow] = useState<TaskResponse>();
  const [entity, setEntity] = useState<string>();
  const [notEntity, setNotEntity] = useState<boolean>();

  const { data: taskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: dataTask?._id } },
    pause: !dataTask?._id
  });

  useEffect(() => {
    if (tasks) {
      const data: Task = tasks.find((item) => item.key === ModuleKeys.ENTITY_DOCS);
      setDataTask(data);

      if (data?.responses.length > 0)
        setStepNow(data?.responses.reduce((max, obj) => obj.step > max.step ? obj : max, data?.responses[0]));

      const step1 = data?.responses?.find((item: any) => item.step === 1);

      if (step1 && !step1.metadata.entity)
        setNotEntity(true);
    }
  }, [tasks]);
  
  useEffect(() => {
    if (entities && loan) {
      const _entity = entities.find((item) => item.loans.find((item2) => item2._id === loan._id));
      setEntity(_entity?.name);
    }
  }, [entities, loan]);
  
  useEffect(() => {
    if (ownerSelect) getEntitiesByOwner();
  }, [ownerSelect]);

  const getResponse = (key: string) =>
    taskResponses?.find((item) => item.key === key) ?? dataTask?.responses?.find((item) => item.key === key);

  const getTotalApproved = () =>
    (taskResponses ?? dataTask?.responses)?.filter((item: any) => item.state !== TaskState.Draft && item.state !== TaskState.RequestError)?.length;

  const generateCirclesFooter: any = () => {
    const responses = taskResponses ?? dataTask?.responses;
    const addAnotherDocs = responses?.filter(item => item.key === 'doc-another');

    const docs = addAnotherDocs?.map(item =>
      ({ state: item.state, name: item.label })
    ) || [];
    return docs;
  };

  const redirectEntityGuarantor = () => setTaskOpenModal('entity_guarantor');
  const isComingSoon = dataTask?.state === TaskState.ComingSoon;

  const entityForLoan = entities?.find((item) => item.loans?.find((item2) => item2._id === loan._id));
  const isConfirmDocs = (entityForLoan?.loans?.length ?? 0) > 1 &&
    dataTask?.responses?.some((item: any) => item.metadata?.viewConfirm);
  const docKeysOrder = isConfirmDocs ? ENTITY_DOCS_ORDER_CONFIRM : Object.keys(ENTITY_DOCS_RESPONSES);

  const card: CardLoanTasksProps =
  {
    key: ModuleKeys.ENTITY_DOCS,
    title: <h4 className={`h4 ${isComingSoon && 'disabled'}`}>Entity Documents and Details</h4>,
    disabled: isComingSoon,
    steps: !notEntity ? 1 : 2,
    stateTooltipHtml: `<span>${'Please complete '}</span><b>Entity Ownership And Guarantors</b><span> to unlock this</span>`,
    stepCurrent: stepNow?.step || 0,
    type: 'Major task',
    state: dataTask?.state,
    actions: dataTask ? [<EntityDocs key={1} task={dataTask} />] : [],
    text: !isComingSoon ? (
      <p className="body-card">
        My entity is <span className="body-card-bold-line">{entity}</span>
      </p>
    ) : (
      <p className={`body-card ${isComingSoon && 'disabled'}`}>
        Create or select an entity{' '}
        <span
          className="body-card-bold-line link link-underline"
          onClick={redirectEntityGuarantor}
        >
          here
        </span>{' '}
        to begin this task
      </p>
    ),
    text2: (
      <div className='docs-section'>
        <div className='labels'>
          <p className={`body-regular body-regular-bold ${isComingSoon && 'disabled'}`}>
            Document status
          </p>
          <p className={`body-card ${isComingSoon && 'disabled'}`}>
            {getTotalApproved()}/{(generateCirclesFooter()?.length || 0) + 4} submitted
          </p>
        </div>
        <div className={`circles-tags ${isComingSoon && 'disabled'}`}>
          {docKeysOrder.map((key) => {
            let response = getResponse(ENTITY_DOCS_RESPONSES[key]);
            let _state = response?.state || 'DRAFT';

            return (<CircleTag
              key={key}
              state={_state}
              tagKey={_state}
              isIconState={true}
              textTwo={ENTITY_DOCS_LABEL[key]}
            />);
          })}
          {generateCirclesFooter().map((item: any, index: number) => {
            let _state = item.state || 'DRAFT';

            return (<CircleTag
              key={index}
              state={_state}
              tagKey={_state}
              isIconState={true}
              textTwo={item.name}
            />);
          })}
        </div>
      </div>
    ),
  };

  if (!dataTask)
    return { card: null };

  return {
    card,
    dataTask
  };
};

export default useCardEntityDocs;